<template>
  <form @submit.prevent="onSubmit">
    <v-card>
      <v-card-title v-if="create">
        <h1>New Product</h1>
      </v-card-title>

      <v-card-text>
        <div v-if="loading" class="d-flex justify-center align-center">
          <v-progress-linear :size="50" indeterminate color="primary" dark></v-progress-linear>
        </div>

        <v-container fluid grid-list-md>
          <v-layout row class="product--layout" wrap>
            <v-flex xs12 sm6 md4>
              <product-image :src="productThumbnail"></product-image>
              <v-btn block color="primary" dark @click="openThumbnailFileDialog">
                <v-icon left>mdi-upload</v-icon>Upload Thumbnail<br>(PNG | Max 2048KB)
              </v-btn>
              <input
                v-show="false"
                type="file"
                accept="image/png"
                ref="thumbnailFileDialog"
                @change="displayThumbnail"
              >
            </v-flex>
            <v-flex xs12 sm6 md8>
              <v-layout row wrap class="layout-wrap-push-top">
                <v-flex xs12 sm12 md6 lg4 v-for="field in showFields" :key="field.key">
                  <template v-if="field.type === 'text'">
                    <v-text-field
                      :label="field.label"
                      :value="getProperty(product, field.key)"
                      @change="updateProperty(product, field.key, $event)"
                      :required="field.required || false"
                      :loading="field.loading"
                      :error="field.error && field.error.length > 0"
                      :error-messages="field.error"
                      :placeholder="field.placeholder || null"
                      :readonly="field.readonly"
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'number'">
                    <v-text-field
                      :label="field.label"
                      :value="getProperty(product, field.key)"
                      @change="updateProperty(product, field.key, $event)"
                      :required="field.required || false"
                      :loading="field.loading"
                      :type="'number'"
                      :min="field.min"
                      :step="field.step || 'any'"
                      :error="field.error && field.error.length > 0"
                      :error-messages="field.error"
                      :placeholder="field.placeholder || null"
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'rich-text'">
                    <v-text-field
                      :label="field.label"
                      @click="onDescriptionFocus(field.key)"
                      :value="getProperty(product, field.key)"
                      :loading="field.loading"
                      :error="field.error && field.error.length > 0"
                      :error-messages="field.error"
                      readonly
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'select'">
                    <v-autocomplete
                      :label="field.label"
                      :items="field.items"
                      :value="getProperty(product, field.key)"
                      @input="updateProperty(product, field.key, $event)"
                      :required="field.required"
                      :loading="field.loading"
                      :error="field.error && field.error.length > 0"
                      :error-messages="field.error"
                      :readonly="field.readonly"
                    ></v-autocomplete>
                  </template>

                  <template v-if="field.type === 'autocomplete'">
                    <v-autocomplete
                      :label="field.label"
                      :items="field.items"
                      :value="getProperty(product, field.key)"
                      @input="updateProperty(product, field.key, $event)"
                      :multiple="field.multiple"
                      :loading="field.loading"
                      :error="field.error && field.error.length > 0"
                      :error-messages="field.error"
                      :readonly="field.readonly"
                    ></v-autocomplete>
                  </template>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-divider v-if="!create"></v-divider>
      <v-card-text v-if="!create">
        <v-layout justify-space-around row wrap>
          <v-flex xs12 justify-center>
            <h2 class="text-xs-center">Existing Images</h2>
          </v-flex>
          <draggable v-model="imagesData" :options="{ animation: 100 }">
            <div v-for="(image, index) in imagesData" :key="index" style="display: inline-block">
              <div :src="getImageSrc(image)" :style="getImageStyle(image)">
                <v-btn
                  color="error"
                  icon
                  style="position: absolute; right: 0px;"
                  small
                  @click="removeImage(image)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </draggable>
        </v-layout>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-text>
        <v-btn block color="primary" dark @click="openFileDialog">
          <v-icon left>mdi-upload</v-icon>Upload Images<br>(PNG/JPEG | Max 2048KB)
        </v-btn>
        <input
          v-show="false"
          type="file"
          accept="image/png, image/jpeg"
          ref="imagesDialog"
          @change="displayImage"
          multiple
        >
        <v-layout justify-center row>
          <div v-for="(image, index) in images" :key="index" style="display: inline-block">
            <div :src="getImageSrc(image)" :style="getImageStyle(image)"></div>
          </div>
        </v-layout>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end">
        <v-btn v-if="create" @click="cancel" color="error" :disabled="loading">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          type="submit"
          color="primary"
          class="submit-btn"
          :disabled="!canSubmit"
          :loading="loading"
        >{{ create ? 'Create' : 'Update' }}</v-btn>
      </v-card-actions>

      <rich-text-editor-modal
        v-model="richDialog.show"
        :content="product[richDialog.target]"
        @update:content="updateProperty(product, richDialog.target, $event)"
      ></rich-text-editor-modal>
    </v-card>
  </form>
</template>

<script>
  // Components
  import ProductImage from '@/components/Product/Image'
  import RichTextEditorModal from '@/components/Common/RichTextEditorModal'
  import Confirmation from '@/components/Common/Confirmation'

  import _ from 'lodash'

  // Mixins
  import productMixin from '@/mixins/product'
  import stringMixin from '@/mixins/string'
  import dateMixin from '@/mixins/date'

  // Services
  import productService from '@/services/product'

  import draggable from 'vuedraggable'

  import uuid from 'uuid/v4'

  export default {
    name: 'product-detail',

    components: {
      ProductImage,
      RichTextEditorModal,
      draggable,
      Confirmation
    },

    props: {
      create: {
        type: Boolean,
        default: false
      },
    },

    mixins: [
      productMixin,
      stringMixin,
      dateMixin
    ],

    data () {
      return {
        loading: false,

        content: '',

        showVideoPreview: false,

        richDialog: {
          show: false,
          target: ''
        },

        product: {},
        productLists: [],

        thumbnail: '',
        images: [],
        imagesData: [],

        fields: [
          {
            label: 'Name (繁體)',
            key: 'name_zh_hk',
            type: 'text',
            required: true
          },
          {
            label: 'Name (簡體)',
            key: 'name_zh_cn',
            type: 'text',
            required: true
          },
          {
            label: 'Name (EN)',
            key: 'name_en',
            type: 'text',
            required: true
          },
          {
            label: 'Description (繁體)',
            key: 'description_zh_hk',
            type: 'rich-text',
            required: false
          },
          {
            label: 'Description (簡體)',
            key: 'description_zh_cn',
            type: 'rich-text',
            required: false
          },
          {
            label: 'Description (EN)',
            key: 'description_en',
            type: 'rich-text',
            required: false
          },
          // {
          //   label: 'SKU',
          //   key: 'sku',
          //   type: 'text',
          //   required: true,
          //   placeholder: 'Barcode number or UUID'
          // },
          {
            label: 'Price',
            key: 'recommended_price',
            type: 'number',
            step: 0.1,
            min: 0,
            required: true
          },
          {
            label: 'Size',
            key: 'size',
            type: 'text',
            required: false
          },
          {
            label: 'Coil Number',
            key: 'coil_number',
            type: 'select',
            required: true,
            items: [
              { text: '4', value: '4' },
              { text: '5', value: '5' },
              { text: '7', value: '7' },
              { text: '9', value: '9' },
              { text: '11', value: '11' },
              { text: '14', value: '14' }
            ]
          }
        ]
      }
    },

    computed: {
      showFields: function () {
        return this.fields.filter(field => field.hide !== true)
      },

      productThumbnail () {
        return this.thumbnail || this.getProductThumbnail(this.product)
      },

      maxUploadImageCount () {
        if (this.create) {
          return 6
        }
        return 6 - this.imagesData.length
      },

      canSubmit () {
        return true
      },
    },

    created () {
      if (this.$route.params.id) {
        this.loading = true
        productService.get(this.$route.params.id, {})
          .then(({ data }) => {
            this.productData = data.data
          })
          .catch(() => {
            // catch error
          })
          .finally(() => {
            this.loading = false
            this.loadForm()
          })
      } else {
        this.create = true
        this.loadForm()
      }
    },

    methods: {
      loadForm () {
        // Add fields' decorator
        this.fields.forEach(field => {
          this.$set(field, 'loading', false)
          this.$set(field, 'error', [])
        })

        if (!this.create) {
          this.product = Object.assign({}, this.productData, {
            product_list_ids: this.productData.product_lists.map(productList => productList.id)
          })
        } else {
          this.resetForm()
        }

        this.imagesData = this.product.images_data || []

        this.loading = false
      },

      getProperty (object, key) {
        return _.get(object, key)
      },

      updateProperty (object, key, value) {
        let result
        result = _.set(object, key, value)

        if (this.create) {
          return result
        }

        // Patch the property
        const field = this.fields.find(field => field.key === key)

        // validation for "required"
        if (field.required && (value === null || value === '')) {
          const errorMessage = field.label + ' cannot be empty'
          field.error.push(errorMessage)
          this.$toasted.error(errorMessage, {
            duration: 5000
          })
          return
        }

        this.resetField(field)
      },

      resetField (field) {
        field.loading = false
        field.error = []
      },

      resetForm () {
        this.product = {
          sku: uuid(),
          name_zh_hk: null,
          name_zh_cn: null,
          name_en: null,
          recommended_price: null,
          size: null,
          description_zh_hk: null,
          description_zh_cn: null,
          description_en: null,
          coil_number: null,
        }

        this.images = []
        this.imagesData = []
      },

      cancel () {
        this.resetForm()
        this.close()
      },

      close () {
        this.$emit('close')
      },

      onDescriptionFocus (field) {
        this.richDialog.show = true
        this.richDialog.target = field
      },

      openFileDialog () {
        this.images = []
        this.$refs.imagesDialog.value = ''
        this.$refs.imagesDialog.click()
      },

      openThumbnailFileDialog () {
        this.thumbnail = ''
        this.$refs.thumbnailFileDialog.value = ''
        this.$refs.thumbnailFileDialog.click()
      },

      async onSubmit () {
        this.loading = true

        const formData = new FormData()

        for (const key in this.fields) {
          const field = this.fields[key]

          // validation for "required"
          const value = this.product[field.key]

          if (field.required && (value === null || value === '')) {
            const errorMessage = field.label + ' cannot be empty'
            field.error.push(errorMessage)
            this.$toasted.error(errorMessage).goAway(5000)
            this.loading = false
            return
          }

          if (value !== null) {
            if (value instanceof Array) {
              for (let v of value) {
                formData.append(`${field.key}[]`, v)
              }
            } else {
              formData.append(field.key, value)
            }
          }
        }

        // check thumbnail
        if (this.create && !this.$refs.thumbnailFileDialog.value) {
          this.$toasted.error('Thumbnail cannot be empty').goAway(5000)
          this.loading = false
          return
        } else {
          if (this.$refs.thumbnailFileDialog.value) {
            formData.append('thumbnail', this.$refs.thumbnailFileDialog.files[0], 'thumbnail.png')
          }
        }

        // check product images
        if (this.create) {
          if (!this.$refs.imagesDialog.files.length) {
            this.$toasted.error('Please choose an image for product').goAway(5000)
            this.loading = false
            return
          } else {
            Array.from(this.$refs.imagesDialog.files).forEach(file => {
              formData.append('images[]', file)
            })
          }
        } else {
          if (this.imagesData.length === 0 && !this.$refs.imagesDialog.files.length) {
            this.$toasted.error('Please choose an image for product').goAway(5000)
            this.loading = false
            return
          } else {
            Array.from(this.$refs.imagesDialog.files).forEach(file => {
              formData.append('images[]', file)
            })

            this.imagesData.forEach(image => {
              formData.append('images_data[]', image)
            })
          }
        }

        try {
          if (this.create) {
            const { data } = await productService.store(formData)
            // Notify parent to insert new row
            this.$emit('created', data.data)
            this.$toasted.success(`Created product: ${this.product.name_zh_hk}`).goAway(5000)

            // reset form in background
            this.$nextTick(() => {
              this.resetForm()
            })
          } else {
            await productService.patch(this.product.id, formData)
            this.$toasted.success(`Updated product: ${this.product.name_zh_hk}`).goAway(5000)
            this.$router.go(0)
          }
        } catch (error) {
          this.$toasted.error('Failed to edit product: ' + error.toString()).goAway(5000)

          if (error.response.data.data) {
            error.response.data.data.forEach(msg => {
              this.$toasted.error(msg).goAway(5000)
            })
          }
        } finally {
          this.loading = false
        }
      },

      displayImage () {
        const files = this.$refs.imagesDialog.files

        if (files.length > this.maxUploadImageCount) {
          this.$toasted.error(`You can only choose maximum ${this.maxUploadImageCount} images for the product`).goAway(5000)
          this.$refs.imagesDialog.value = ''
          this.images = []
          return
        }

        for (let i = 0; i < files.length; i++) {
          const reader = new FileReader()
          reader.onload = () => {
            this.images.push(reader.result)
          }
          reader.onerror = error => { this.$toasted.error(error).goAway(5000) }
          reader.readAsDataURL(files[i])
        }
      },

      displayThumbnail () {
        const files = this.$refs.thumbnailFileDialog.files

        const reader = new FileReader()
        reader.onload = () => {
          this.thumbnail = reader.result
        }
        reader.onerror = error => { this.$toasted.error(error).goAway(5000) }
        reader.readAsDataURL(files[0])
      },

      getImageStyle (image) {
        return {
          position: 'relative',
          display: 'inline-block',
          height: '205.5px',
          width: '151px',
          backgroundImage: `url(${this.getImageSrc(image)})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'black',
          marginLeft: '10px',
          marginRight: '10px'
        }
      },

      getImageSrc (image) {
        if (image.match(/^data:image/)) {
          return image
        }

        return `${process.env.PRODUCT_API_HOST}/products/${this.product.product_lists[0].folder}/assets/${image}`
      },

      removeImage (selectedImage) {
        this.imagesData = this.imagesData.filter(image => {
          return image !== selectedImage
        })
      },
    }
  }
</script>

<style lang="sass" scoped>
</style>



// WEBPACK FOOTER //
// src/components/Product/Detail.vue